import { useEffect, useRef, useState } from "react";
import { FaChevronDown } from "react-icons/fa";

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

export function Select({
  value,
  options,
  onChange,
  disabled,
  placeholder,
  className,
  triggerClassName,
  menuClassName,
  footer,
}: {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  menuClassName?: string;
  /** Extra non-selectable content pinned below the option list, e.g. an inline "create new" form. Receives a close() to dismiss the menu once the action completes. */
  footer?: (close: () => void) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.focus();
    function onDocPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [open]);

  function openMenu() {
    if (disabled) return;
    setActiveIndex(Math.max(0, options.findIndex((o) => o.value === value)));
    setOpen(true);
  }

  function onTriggerKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openMenu();
    }
  }

  function onMenuKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((i) => Math.min(options.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const option = options[activeIndex];
      if (option) { onChange(option.value); setOpen(false); }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  }

  return (
    <div ref={rootRef} className={`select-root${className ? ` ${className}` : ""}`} data-open={open}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
        className={`select-trigger${triggerClassName ? ` ${triggerClassName}` : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="select-value">{selected?.label ?? placeholder ?? ""}</span>
        <FaChevronDown className="select-caret" size={10} />
      </button>
      {open && (
        <div className={`select-menu${menuClassName ? ` ${menuClassName}` : ""}`}>
          <ul
            ref={menuRef}
            role="listbox"
            tabIndex={-1}
            onKeyDown={onMenuKeyDown}
            className="select-options"
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                role="option"
                aria-selected={option.value === value}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => { onChange(option.value); setOpen(false); }}
                className={`select-option${option.value === value ? " selected" : ""}${index === activeIndex ? " active" : ""}`}
              >
                <span className="select-option-label">{option.label}</span>
                {option.description && (
                  <span className="select-option-description">{option.description}</span>
                )}
              </li>
            ))}
          </ul>
          {footer && (
            <div className="select-footer">
              {footer(() => setOpen(false))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
